import React, { useState, useEffect, useRef } from "react";
import {
  FolderOpen,
  File,
  Mail,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Trash2,
  X,
  Calendar,
  Paperclip,
  Download,
  Maximize,
  Minimize,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { getToken, base_url } from "../config/config";

// ===========================
// FileViewer Component with Image Gallery
// ===========================
const handlePrint = (imageFiles, file) => {
  // Get the current file to print
  const currentFile = imageFiles ? imageFiles : file;

  // Create a new window to print the content
  const printWindow = window.open("", "_blank");

  // For images
  if (currentFile.file.mimeType.startsWith("image/")) {
    printWindow.document.write(`
        <html>
          <head>
            <title>Print: ${currentFile.file.originalName}</title>
            <style>
              body {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                margin: 0;
              }
              img {
                max-width: 100%;
                max-height: 100vh;
                object-fit: contain;
              }
              @media print {
                body {
                  height: auto;
                }
              }
            </style>
          </head>
          <body>
            <img src="${currentFile.file.s3Url}" alt="${currentFile.file.originalName}" />
            <script>
              // Auto print when everything is loaded
              window.onload = function() {
                setTimeout(() => {
                  window.print();
                  // Uncomment below if you want the window to close after printing
                  // window.close();
                }, 500);
              }
            </script>
          </body>
        </html>
      `);
    printWindow.document.close();
  }
  // For PDFs and other documents
  else {
    printWindow.location.href = currentFile.file.s3Url;
    // Give the browser time to load the document before triggering print
    setTimeout(() => {
      printWindow.print();
    }, 1000);
  }
};

const FileViewer = ({ file, onClose, files }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageFiles, setImageFiles] = useState([]);

  const containerRef = useRef(null);

  // Find all image files and create a gallery if current file is an image
  useEffect(() => {
    if (file.file.mimeType.startsWith("image/")) {
      // Find all image files to create a gallery
      const allImages = files
        ? files.filter((f) => f.file.mimeType.startsWith("image/"))
        : [file];

      // Set the images array
      setImageFiles(allImages);

      // Find the index of the current file in the images array
      const index = allImages.findIndex((img) => img._id === file._id);
      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [file, files]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Keyboard navigation for gallery
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (file.file.mimeType.startsWith("image/")) {
        if (e.key === "ArrowLeft") {
          navigateGallery(-1);
        } else if (e.key === "ArrowRight") {
          navigateGallery(1);
        } else if (e.key === "Escape") {
          onClose();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, imageFiles.length]);

  const navigateGallery = (direction) => {
    if (!imageFiles.length) return;

    setZoomLevel(1); // Reset zoom when changing images
    setOffsetX(0);

    setCurrentIndex((prevIndex) => {
      const newIndex = prevIndex + direction;
      if (newIndex < 0) {
        return imageFiles.length - 1; // Wrap to the end
      } else if (newIndex >= imageFiles.length) {
        return 0; // Wrap to the beginning
      }
      return newIndex;
    });
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  // Mouse events for dragging
  const handleMouseDown = (e) => {
    if (zoomLevel > 1 || !file.file.mimeType.startsWith("image/")) return;

    setIsDragging(true);
    setStartX(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const diff = e.clientX - startX;
    setOffsetX(diff);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;

    const threshold = 100; // Minimum drag distance to trigger navigation
    if (offsetX > threshold) {
      navigateGallery(-1);
    } else if (offsetX < -threshold) {
      navigateGallery(1);
    }

    setIsDragging(false);
    setOffsetX(0);
  };

  // Touch events for mobile swiping
  const handleTouchStart = (e) => {
    if (zoomLevel > 1 || !file.file.mimeType.startsWith("image/")) return;

    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (zoomLevel > 1 || !file.file.mimeType.startsWith("image/")) return;

    const diff = e.touches[0].clientX - touchStartX;
    setOffsetX(diff);
  };

  const handleTouchEnd = () => {
    if (!file.file.mimeType.startsWith("image/")) return;

    const threshold = 50; // Smaller threshold for touch
    if (offsetX > threshold) {
      navigateGallery(-1);
    } else if (offsetX < -threshold) {
      navigateGallery(1);
    }

    setOffsetX(0);
  };

  // Zoom controls
  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.5, 1));
  };

  const handleDownload = () => {
    const currentFile = imageFiles[currentIndex];
    const link = document.createElement("a");
    link.href = currentFile.file.s3Url;
    link.download = currentFile.file.originalName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Animation style based on current drag offset
  const getTransformStyle = () => {
    if (!file.file.mimeType.startsWith("image/")) return {};

    const baseTransform = `scale(${zoomLevel})`;
    if (isDragging || offsetX !== 0) {
      return {
        transform: `${baseTransform} translateX(${offsetX / zoomLevel}px)`,
      };
    }
    return { transform: baseTransform };
  };

  const getFileComponent = () => {
    // Show image gallery for images if we're on an image file
    if (file.file.mimeType.startsWith("image/") && imageFiles.length > 0) {
      const currentFile = imageFiles[currentIndex];

      return (
        <div
          className="relative flex-1 overflow-hidden flex items-center justify-center"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Left Navigation Button - only show if we have multiple images */}
          {imageFiles.length > 1 && (
            <button
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 p-2 rounded-full text-white z-10 hover:bg-opacity-70 transition-opacity"
              onClick={() => navigateGallery(-1)}
            >
              <ChevronLeft size={24} />
            </button>
          )}

          {/* Image with zoom/transform */}
          <div
            className="transition-transform duration-200 ease-out"
            style={getTransformStyle()}
          >
            <img
              src={currentFile.file.s3Url}
              alt={currentFile.file.originalName}
              className="max-h-full max-w-full object-contain"
            />
          </div>

          {/* Right Navigation Button - only show if we have multiple images */}
          {imageFiles.length > 1 && (
            <button
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 p-2 rounded-full text-white z-10 hover:bg-opacity-70 transition-opacity"
              onClick={() => navigateGallery(1)}
            >
              <ChevronRight size={24} />
            </button>
          )}
        </div>
      );
    }
    // For non-image files, use the original logic
    else if (file.file.mimeType === "application/pdf") {
      return (
        <iframe
          src={file.file.s3Url}
          className="w-full h-full"
          title={file?.file?.originalName}
        />
      );
    }
    // Default for other file types
    else {
      return (
        <div className="text-center p-4">
          <a
            href={file.file.s3Url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700 underline"
          >
            Download {file?.file?.originalName}
          </a>
        </div>
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 pt-20">
      <div
        ref={containerRef}
        className="bg-white dark:bg-gray-900 w-full max-w-4xl h-full max-h-[85vh] flex flex-col rounded-xl overflow-hidden shadow-2xl relative"
      >
        {/* Close button - absolute positioned for visibility */}
        <button
          onClick={onClose}
          className="absolute right-6 top-6 p-2 hover:bg-gray-100 rounded-full dark:hover:bg-gray-700 z-40 transition-colors"
          title="Close"
        >
          <X size={24} className="text-gray-600 dark:text-gray-300" />
        </button>

        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate pr-10">
            {imageFiles.length > 0
              ? `${imageFiles[currentIndex].file.originalName} (${
                  currentIndex + 1
                }/${imageFiles.length})`
              : file?.file?.originalName}
          </h3>
          <div className="flex items-center gap-2 mt-4">
            {/* Controls for images */}
            {file.file.mimeType.startsWith("image/") && (
              <>
                <button
                  onClick={handleDownload}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Download"
                >
                  <Download
                    size={20}
                    className="text-gray-600 dark:text-gray-300"
                  />
                </button>
                <button
                  onClick={handleZoomIn}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  disabled={zoomLevel >= 3}
                  title="Zoom in"
                >
                  <ZoomIn
                    size={20}
                    className="text-gray-600 dark:text-gray-300"
                  />
                </button>
                <button
                  onClick={handleZoomOut}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  disabled={zoomLevel <= 1}
                  title="Zoom out"
                >
                  <ZoomOut
                    size={20}
                    className="text-gray-600 dark:text-gray-300"
                  />
                </button>
                <button
                  onClick={toggleFullscreen}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Toggle fullscreen"
                >
                  {isFullscreen ? (
                    <Minimize
                      size={20}
                      className="text-gray-600 dark:text-gray-300"
                    />
                  ) : (
                    <Maximize
                      size={20}
                      className="text-gray-600 dark:text-gray-300"
                    />
                  )}
                </button>
              </>
            )}
            <button
              onClick={() => handlePrint(file[currentIndex], file)}
              className="ml-auto px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Print
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-950">
          {getFileComponent()}
        </div>

        {/* Thumbnail bar for image gallery */}
        {file.file.mimeType.startsWith("image/") && imageFiles.length > 1 && (
          <div className="flex-shrink-0 p-4 bg-gray-100 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-center gap-2 overflow-x-auto py-2">
              {imageFiles.map((img, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentIndex(index);
                    setZoomLevel(1);
                  }}
                  className={`w-16 h-16 flex-shrink-0 border-2 rounded-lg overflow-hidden transition-all ${
                    currentIndex === index
                      ? "border-blue-500 opacity-100 ring-2 ring-blue-400 ring-offset-2"
                      : "border-gray-300 opacity-60 hover:opacity-100"
                  }`}
                >
                  <img
                    src={img.file.s3Url}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ===========================
// EmailViewer Component
// ===========================
const EmailViewer = ({ email, onClose }) => {
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  const handleAttachmentClick = (attachment) => {
    // Create a file-like structure that matches what FileViewer expects
    setSelectedAttachment({
      file: {
        mimeType: attachment.contentType,
        originalName: attachment.filename,
        s3Url: `data:${attachment.contentType};base64,${attachment.content}`,
      },
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl h-full max-h-[90vh] m-4 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Mail className="text-blue-500" />
            <h3 className="text-lg font-medium">Email Details</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-6">
            {/* Email Header */}
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <span className="text-gray-500 w-20">From:</span>
                <span className="font-medium">{email.from}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-gray-500 w-20">Subject:</span>
                <span className="font-medium">{email.subject}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-gray-500 w-20">Date:</span>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-gray-400" />
                  <span>{new Date(email.date).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Email Body */}
            <div className="border-t pt-6">
              <div className="prose max-w-none">
                <p className="whitespace-pre-wrap">{email.body}</p>
              </div>
            </div>

            {/* Attachments */}
            {email.attachments && email.attachments.length > 0 && (
              <div className="border-t pt-6">
                <h4 className="text-sm font-medium text-gray-500 mb-4">
                  Attachments
                </h4>
                <div className="space-y-2">
                  {email.attachments.map((attachment, index) => (
                    <button
                      key={index}
                      onClick={() => handleAttachmentClick(attachment)}
                      className="w-full flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left"
                    >
                      <Paperclip size={16} className="text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {attachment.filename}
                      </span>
                      <span className="text-xs text-gray-400">
                        ({Math.round(attachment.size / 1024)} KB)
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedAttachment && (
        <FileViewer
          file={selectedAttachment}
          onClose={() => setSelectedAttachment(null)}
        />
      )}
    </div>
  );
};

// ===========================
// CategorySelectionModal
// ===========================
const CategorySelectionModal = ({
  file,
  categories,
  categoryIds,
  onClose,
  onSave,
}) => {
  // Local state for storing which main category/subcategory the user picks
  const [selectedMainCat, setSelectedMainCat] = useState("");
  const [selectedSubCat, setSelectedSubCat] = useState("");
  const [step, setStep] = useState(1); // 1 = main category, 2 = subcategory
  const [searchTerm, setSearchTerm] = useState("");

  // If the file has an existing category, we can set that as default
  useEffect(() => {
    if (file?.category?.labelId?.name) {
      setSelectedMainCat(file.category.labelId.name);
      setStep(2); // Go directly to subcategory selection if main category exists
    }
    if (file?.category?.name) {
      setSelectedSubCat(file.category.name);
    }
  }, [file]);

  // Called when user clicks "Save" in the modal
  const handleSave = () => {
    if (!selectedSubCat) return;
    onSave(selectedSubCat);
  };

  // Filter categories based on search term
  const filteredCategories = Object.keys(categories).filter((cat) =>
    cat.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSubCategories = selectedMainCat
    ? categories[selectedMainCat]?.filter((subCat) =>
        subCat.toLowerCase().includes(searchTerm.toLowerCase())
      ) || []
    : [];

  // Category icon mapping
  const getCategoryIcon = (categoryName) => {
    const icons = {
      Documents: "📄",
      Images: "🖼️",
      "Profile Image": "👤",
      Videos: "🎥",
      Audio: "🎵",
      Archives: "📦",
      Drawings: "📐",
      Certificates: "🏆",
      Reports: "📊",
      Specifications: "📋",
      default: "📁",
    };
    return icons[categoryName] || icons.default;
  };

  const getSubCategoryIcon = (subCategoryName) => {
    const icons = {
      "Technical Drawings": "🔧",
      "CAD Files": "⚙️",
      Blueprints: "📘",
      Schematics: "🔌",
      Photos: "📷",
      Screenshots: "🖥️",
      Scans: "📄",
      Invoices: "🧾",
      Contracts: "📋",
      Manuals: "📖",
      Safety: "⚠️",
      Quality: "✅",
      default: "📄",
    };
    return icons[subCategoryName] || icons.default;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl border border-gray-200">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#385F65] via-[#2d4f56] to-[#385F65] text-white p-6 shadow-lg">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold mb-2 drop-shadow-sm">
                📁 Kategorie-Auswahl
              </h2>
              <p className="text-[#b8d4d7] text-sm">
                {step === 1
                  ? "Wählen Sie eine Hauptkategorie für Ihre Datei"
                  : `Wählen Sie eine Unterkategorie unter "${selectedMainCat}"`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-all duration-200 shadow-sm"
            >
              <X size={24} />
            </button>
          </div>

          {/* Progress indicator */}
          <div className="mt-4 flex items-center">
            <div
              className={`flex items-center ${step >= 1 ? "text-white" : "text-[#b8d4d7]"}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-md ${step >= 1 ? "bg-white text-[#385F65]" : "bg-[#6b8b91] text-[#385F65]"}`}
              >
                1
              </div>
              <span className="ml-2 text-sm font-medium">Hauptkategorie</span>
            </div>
            <div className="flex-1 h-0.5 bg-[#6b8b91] mx-4 rounded-full"></div>
            <div
              className={`flex items-center ${step >= 2 ? "text-white" : "text-[#b8d4d7]"}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-md ${step >= 2 ? "bg-white text-[#385F65]" : "bg-[#6b8b91] text-[#385F65]"}`}
              >
                2
              </div>
              <span className="ml-2 text-sm font-medium">Unterkategorie</span>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-6 border-b border-[#385F65] border-opacity-20 bg-gradient-to-r from-gray-50 to-white">
          <div className="relative">
            <input
              type="text"
              placeholder={
                step === 1
                  ? "Hauptkategorien durchsuchen..."
                  : "Unterkategorien durchsuchen..."
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-[#385F65] border-opacity-30 rounded-lg focus:ring-2 focus:ring-[#385F65] focus:border-[#385F65] text-sm shadow-sm bg-white"
            />
            <div className="absolute left-3 top-3.5">
              <svg
                className="w-4 h-4 text-[#385F65] opacity-60"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto bg-gradient-to-b from-white to-gray-50">
          {step === 1 ? (
            /* Main Category Selection */
            <div>
              <h3 className="text-lg font-semibold text-[#385F65] mb-4">
                Hauptkategorie auswählen ({filteredCategories.length} verfügbar)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredCategories.map((mainCat) => (
                  <button
                    key={mainCat}
                    onClick={() => {
                      setSelectedMainCat(mainCat);
                      setStep(2);
                      setSearchTerm("");
                    }}
                    className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-lg hover:scale-105 ${
                      selectedMainCat === mainCat
                        ? "border-[#385F65] bg-[#385F65] bg-opacity-10 text-[#385F65] shadow-lg ring-2 ring-[#385F65] ring-opacity-20"
                        : "border-[#385F65] border-opacity-20 bg-white text-gray-700 hover:border-[#385F65] hover:bg-[#385F65] hover:bg-opacity-5 shadow-sm"
                    }`}
                  >
                    <div className="text-3xl mb-2">
                      {getCategoryIcon(mainCat)}
                    </div>
                    <div className="font-medium text-sm text-center">
                      {mainCat}
                    </div>
                    <div className="text-xs text-[#385F65] opacity-70 mt-1">
                      {categories[mainCat]?.length || 0} Unterkategorien
                    </div>
                  </button>
                ))}
              </div>

              {filteredCategories.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">🔍</div>
                  <p className="text-[#385F65] opacity-70">
                    Keine Kategorien gefunden für "{searchTerm}"
                  </p>
                </div>
              )}
            </div>
          ) : (
            /* Subcategory Selection */
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#385F65]">
                  Unterkategorie in "{selectedMainCat}" auswählen (
                  {filteredSubCategories.length} verfügbar)
                </h3>
                <button
                  onClick={() => {
                    setStep(1);
                    setSearchTerm("");
                  }}
                  className="flex items-center px-3 py-1.5 text-sm text-[#385F65] hover:bg-[#385F65] hover:bg-opacity-10 rounded-lg transition-colors border border-[#385F65] border-opacity-30"
                >
                  <ChevronLeft size={16} className="mr-1" />
                  Zurück zu Hauptkategorien
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredSubCategories.map((subCat) => (
                  <button
                    key={subCat}
                    onClick={() => setSelectedSubCat(subCat)}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 text-left hover:shadow-lg ${
                      selectedSubCat === subCat
                        ? "border-[#4a7c85] bg-[#4a7c85] bg-opacity-15 text-[#385F65] shadow-lg ring-2 ring-[#4a7c85] ring-opacity-30"
                        : "border-[#385F65] border-opacity-20 bg-white text-gray-700 hover:border-[#4a7c85] hover:bg-[#4a7c85] hover:bg-opacity-5 shadow-sm"
                    }`}
                  >
                    <div className="flex items-center">
                      <span className="text-xl mr-3">
                        {getSubCategoryIcon(subCat)}
                      </span>
                      <div>
                        <div className="font-medium text-sm">{subCat}</div>
                        {selectedSubCat === subCat && (
                          <div className="text-xs text-[#4a7c85] mt-1 flex items-center">
                            <svg
                              className="w-3 h-3 mr-1"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Ausgewählt
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {filteredSubCategories.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-4xl mb-2">📂</div>
                  <p className="text-[#385F65] opacity-70">
                    {searchTerm
                      ? `Keine Unterkategorien gefunden für "${searchTerm}"`
                      : `Keine Unterkategorien verfügbar in "${selectedMainCat}"`}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#385F65] border-opacity-20 p-6 bg-gradient-to-r from-gray-50 to-white shadow-inner">
          <div className="flex justify-between items-center">
            <div className="text-sm text-[#385F65]">
              {selectedMainCat && (
                <span className="flex items-center">
                  <span className="mr-2 font-medium">Aktuelle Auswahl:</span>
                  <span className="bg-[#385F65] bg-opacity-15 text-[#385F65] px-3 py-1 rounded-full text-xs font-medium mr-2 border border-[#385F65] border-opacity-30">
                    {selectedMainCat}
                  </span>
                  {selectedSubCat && (
                    <span className="bg-[#4a7c85] bg-opacity-15 text-[#4a7c85] px-3 py-1 rounded-full text-xs font-medium border border-[#4a7c85] border-opacity-30">
                      {selectedSubCat}
                    </span>
                  )}
                </span>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium shadow-sm border border-gray-300"
              >
                Abbrechen
              </button>

              <button
                onClick={handleSave}
                disabled={!selectedSubCat}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all duration-200 shadow-md ${
                  selectedSubCat
                    ? "bg-gradient-to-r from-[#385F65] to-[#4a7c85] text-white hover:from-[#2d4f56] hover:to-[#3d6b73] shadow-lg hover:shadow-xl border border-[#385F65]"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed border border-gray-300"
                }`}
              >
                {selectedSubCat
                  ? "Kategorie speichern"
                  : "Wählen Sie eine Unterkategorie"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===========================
// Main FileManager Component
// ===========================
const FileManager = () => {
  const { anr } = useParams();
  const [files, setFiles] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // For expanded categories & sub-categories
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubCategories, setExpandedSubCategories] = useState({});

  // For file/email viewers
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);

  // For deleting files
  const [deletingFiles, setDeletingFiles] = useState(new Set());

  // For categories
  const [categories, setCategories] = useState({});
  const [categoryIds, setCategoryIds] = useState({});

  // For showing/hiding category selection modal
  const [fileToUpdate, setFileToUpdate] = useState(null);

  useEffect(() => {
    fetchData();
  }, [anr]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const token = getToken();

  // ===========================
  // Fetching documents & emails
  // ===========================
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${base_url}/v1/getFilesByAnr?ANR=${anr}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      // Separate files and emails
      const emailData = data.files.filter((item) => item.email);
      const fileData = data.files.filter((item) => !item.email);

      setFiles(fileData);
      setEmails(emailData);
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // ===========================
  // Fetching all categories/subcategories
  // ===========================
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${base_url}/v1/getAllSubCategories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      const organizedData = {};
      const idMapping = {};

      data.data.forEach((item) => {
        if (item.labelId) {
          const categoryName = item.labelId.name;
          if (!organizedData[categoryName]) {
            organizedData[categoryName] = [];
          }
          organizedData[categoryName].push(item.name);
        } else {
          organizedData[item._id] = item.name;
        }
        idMapping[item.name] = item._id;
      });

      setCategories(organizedData);
      setCategoryIds(idMapping);
    } catch (err) {
      console.error("Error loading categories:", err);
      setError("Failed to load categories.");
    }
  };

  // ===========================
  // Delete file
  // ===========================
  const deleteFile = async (file) => {
    try {
      setDeletingFiles((prev) => new Set([...prev, file._id]));

      const response = await fetch(`${base_url}/v1/removeDocumentFromProject`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          _id: file._id,
          ANR: anr,
        }),
      });

      if (!response.ok) throw new Error("Failed to delete file");
      await fetchData();
    } catch (error) {
      console.error("Error deleting file:", error);
      setError("Failed to delete file");
    } finally {
      setDeletingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(file._id);
        return newSet;
      });
    }
  };

  // ===========================
  // Set as Profile Image
  // ===========================
  const setAsProfileImage = async (file) => {
    console.log("Setting as profile image:", file);
    try {
      setError(null);

      const response = await fetch(`${base_url}/v1/updateProfileImage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ANR: anr,
          documentId: file._id,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to set as profile image");
      }

      const result = await response.json();

      if (result.status === 1) {
        // Show success message (you can customize this)
        console.log("Success:", result.message);

        // Refresh data to show updated categories
        await fetchData();
      } else {
        throw new Error(result.message || "Failed to set as profile image");
      }
    } catch (err) {
      console.error("Error setting profile image:", err);
      setError(`Failed to set as profile image: ${err.message}`);
    }
  };

  // ===========================
  // Assign/Update category
  // ===========================
  const assignCategoryToDocument = async (fileId, categoryId) => {
    setError(null);
    try {
      const response = await fetch(`${base_url}/v1/assignCategoryToDocument`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          categoryId: categoryId,
          _id: fileId,
        }),
      });
      if (!response.ok) {
        throw new Error("Failed to assign category");
      }
      // Re-fetch to update UI
      await fetchData();
    } catch (err) {
      console.error("Error assigning category:", err);
      setError("Failed to assign category");
    }
  };

  // ===========================
  // Called when user saves from the CategorySelectionModal
  // ===========================
  const handleCategorySelect = (subCatName) => {
    if (!fileToUpdate) return;
    const newCategoryId = categoryIds[subCatName];
    if (!newCategoryId) {
      console.error("Could not find category ID for subcategory:", subCatName);
      return;
    }
    // Assign category to the current file and close the modal
    assignCategoryToDocument(fileToUpdate._id, newCategoryId);
    setFileToUpdate(null);
  };

  // ===========================
  // Group files by main/sub-cat
  // ===========================
  const groupedFiles = files.reduce((acc, file) => {
    const categoryName =
      file.category?.labelId == null
        ? file.category?.name
        : file.category?.labelId?.name || "No Category";
    const subCategoryName = file.category?.name || "No Subcategory";

    if (!acc[categoryName]) acc[categoryName] = {};
    if (!acc[categoryName][subCategoryName])
      acc[categoryName][subCategoryName] = [];
    acc[categoryName][subCategoryName].push(file);
    return acc;
  }, {});

  // ===========================
  // Expand/collapse logic
  // ===========================
  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const toggleSubCategory = (category, subCategory) => {
    const key = `${category}-${subCategory}`;
    setExpandedSubCategories((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // ===========================
  // Render
  // ===========================
  if (!anr) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-600">
        No ANR provided
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">
        File Manager - ANR: {anr}
      </h1>

      {/* Emails Section */}
      {emails.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 text-gray-700 flex items-center gap-2">
            <Mail className="text-blue-500" />
            Emails
          </h2>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="divide-y">
              {emails.map((email) => (
                <div key={email._id} className="py-3 first:pt-0 last:pb-0">
                  <button
                    onClick={() => setSelectedEmail(email)}
                    className="w-full text-left flex items-center gap-4 hover:bg-gray-50 p-2 rounded-lg transition-colors"
                  >
                    <Mail size={20} className="text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {email.email.subject}
                      </p>
                      <p className="text-sm text-gray-500">
                        {email.email.from}
                      </p>
                    </div>
                    <span className="text-sm text-gray-400">
                      {new Date(email.email.date).toLocaleDateString()}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Files Section */}
      <div className="space-y-6">
        {Object.entries(groupedFiles).map(([category, subCategories]) => (
          <div key={category} className="bg-white rounded-lg shadow-sm p-4">
            {/* For Profile Image category, show files directly without subcategories */}
            {category === "Profile Image" ? (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <FolderOpen className="text-blue-500" size={24} />
                  <h2 className="text-lg font-semibold text-gray-700">
                    {category}
                  </h2>
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    {Object.values(subCategories).flat().length} files
                  </span>
                </div>

                {/* Display all files directly */}
                <div className="pl-6">
                  {/* Image files as thumbnails */}
                  {Object.values(subCategories)
                    .flat()
                    .some((file) =>
                      file?.file?.mimeType?.startsWith("image/")
                    ) && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {Object.values(subCategories)
                        .flat()
                        .filter((file) =>
                          file?.file?.mimeType?.startsWith("image/")
                        )
                        .map((file) => (
                          <div
                            key={file._id}
                            className="group relative rounded-lg overflow-hidden bg-gray-100 aspect-square hover:shadow-md transition-all"
                          >
                            {/* Image thumbnail */}
                            <button
                              className="w-full h-full flex items-center justify-center"
                              onClick={() => setSelectedFile(file)}
                            >
                              <img
                                src={file.file.s3Url}
                                alt={file?.file?.originalName}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-end">
                                <div className="w-full bg-gradient-to-t from-black to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-2">
                                  <p className="text-white text-xs truncate">
                                    {file?.file?.originalName}
                                  </p>
                                </div>
                              </div>
                            </button>

                            {/* Action buttons */}
                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteFile(file);
                                }}
                                className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                disabled={deletingFiles.has(file._id)}
                              >
                                {deletingFiles.has(file._id) ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                ) : (
                                  <Trash2 size={14} />
                                )}
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Non-image files in list view */}
                  {Object.values(subCategories)
                    .flat()
                    .some(
                      (file) => !file?.file?.mimeType?.startsWith("image/")
                    ) && (
                    <div className="grid grid-cols-1 gap-2">
                      {Object.values(subCategories)
                        .flat()
                        .filter(
                          (file) => !file?.file?.mimeType?.startsWith("image/")
                        )
                        .map((file) => (
                          <div
                            key={file?._id}
                            className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md transition-colors w-full group"
                          >
                            <button
                              className="flex-1 flex items-center gap-2 text-left"
                              onClick={() => setSelectedFile(file)}
                            >
                              <File size={16} className="text-gray-400" />
                              <span className="text-sm text-gray-600">
                                {file?.file?.originalName}
                              </span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteFile(file);
                              }}
                              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded-full transition-opacity"
                              disabled={deletingFiles.has(file?._id)}
                            >
                              {deletingFiles.has(file?._id) ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500" />
                              ) : (
                                <Trash2 size={16} className="text-red-500" />
                              )}
                            </button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Original code for other categories
              <>
                {/* Main Category */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="flex items-center gap-2 w-full text-left mb-4"
                >
                  {expandedCategories[category] ? (
                    <ChevronDown className="text-gray-500" size={20} />
                  ) : (
                    <ChevronRight className="text-gray-500" size={20} />
                  )}
                  <FolderOpen className="text-blue-500" size={24} />
                  <h2 className="text-lg font-semibold text-gray-700">
                    {category}
                  </h2>
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    {Object.values(subCategories).flat().length} files
                  </span>
                </button>

                {/* Subcategories */}
                {expandedCategories[category] && (
                  <div className="pl-6 space-y-4">
                    {Object.entries(subCategories).map(
                      ([subCategory, files]) => {
                        const subCategoryKey = `${category}-${subCategory}`;
                        return (
                          <div
                            key={subCategory}
                            className="border-l-2 border-gray-200 pl-4"
                          >
                            <button
                              onClick={() =>
                                toggleSubCategory(category, subCategory)
                              }
                              className="flex items-center gap-2 w-full text-left mb-2"
                            >
                              {expandedSubCategories[subCategoryKey] ? (
                                <ChevronDown
                                  className="text-gray-500"
                                  size={16}
                                />
                              ) : (
                                <ChevronRight
                                  className="text-gray-500"
                                  size={16}
                                />
                              )}
                              <h3 className="text-md font-medium text-gray-600">
                                {subCategory}
                              </h3>
                              <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-0.5 rounded-full">
                                {files.length} files
                              </span>
                            </button>

                            {/* Files under this subcategory */}
                            {expandedSubCategories[subCategoryKey] && (
                              <div>
                                {/* Image files as thumbnails */}
                                {files.some((file) =>
                                  file.file.mimeType.startsWith("image/")
                                ) && (
                                  <div className="mb-4">
                                    <h4 className="text-sm font-medium text-gray-500 mb-2">
                                      Images
                                    </h4>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                      {files
                                        .filter((file) =>
                                          file.file.mimeType.startsWith(
                                            "image/"
                                          )
                                        )
                                        .map((file) => (
                                          <div
                                            key={file._id}
                                            className="group relative rounded-lg overflow-hidden bg-gray-100 aspect-square hover:shadow-md transition-all"
                                          >
                                            {/* Image thumbnail */}
                                            <button
                                              className="w-full h-full flex items-center justify-center"
                                              onClick={() =>
                                                setSelectedFile(file)
                                              }
                                            >
                                              <img
                                                src={file.file.s3Url}
                                                alt={file?.file?.originalName}
                                                className="w-full h-full object-cover"
                                              />
                                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-end">
                                                <div className="w-full bg-gradient-to-t from-black to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-2">
                                                  <p className="text-white text-xs truncate">
                                                    {file?.file?.originalName}
                                                  </p>
                                                </div>
                                              </div>
                                            </button>

                                            {/* Action buttons overlay */}
                                            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                              {category != "Profile Image" && (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setAsProfileImage(
                                                      file?.file
                                                    );
                                                  }}
                                                  className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800"
                                                >
                                                  Als Profilbild festlegen
                                                </button>
                                              )}
                                              <button
                                                onClick={() =>
                                                  handlePrint(file)
                                                }
                                                className="px-3 py-1 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800"
                                              >
                                                Print
                                              </button>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setFileToUpdate(file);
                                                }}
                                                className="p-1 bg-green-500 text-white rounded-full hover:bg-green-600"
                                                title="Change Category"
                                              >
                                                <ChevronDown size={14} />
                                              </button>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  deleteFile(file);
                                                }}
                                                className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                                title="Delete"
                                                disabled={deletingFiles.has(
                                                  file._id
                                                )}
                                              >
                                                {deletingFiles.has(file._id) ? (
                                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                                                ) : (
                                                  <Trash2 size={14} />
                                                )}
                                              </button>
                                            </div>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}

                                {/* Non-image files in list view */}
                                {files.some(
                                  (file) =>
                                    !file.file.mimeType.startsWith("image/")
                                ) && (
                                  <div>
                                    <h4 className="text-sm font-medium text-gray-500 mb-2">
                                      Other Files
                                    </h4>
                                    <div className="grid grid-cols-1 gap-2">
                                      {files
                                        .filter(
                                          (file) =>
                                            !file.file.mimeType.startsWith(
                                              "image/"
                                            )
                                        )
                                        .map((file) => (
                                          <div
                                            key={file._id}
                                            className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md transition-colors w-full group"
                                          >
                                            {/* Click to view file */}
                                            <button
                                              className="flex-1 flex items-center gap-2 text-left"
                                              onClick={() =>
                                                setSelectedFile(file)
                                              }
                                            >
                                              <File
                                                size={16}
                                                className="text-gray-400"
                                              />
                                              <span className="text-sm text-gray-600">
                                                {file?.file?.originalName}
                                              </span>
                                            </button>

                                            {/* Category Button - opens modal */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setFileToUpdate(file);
                                              }}
                                              className="text-xs font-semibold bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-300"
                                            >
                                              Kategorie ändern
                                            </button>

                                            {/* Delete button */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                deleteFile(file);
                                              }}
                                              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded-full transition-opacity"
                                              disabled={deletingFiles.has(
                                                file._id
                                              )}
                                            >
                                              {deletingFiles.has(file._id) ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500" />
                                              ) : (
                                                <Trash2
                                                  size={16}
                                                  className="text-red-500"
                                                />
                                              )}
                                            </button>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Modals for File/Email Viewers */}
      {selectedFile && (
        <FileViewer
          file={selectedFile}
          onClose={() => setSelectedFile(null)}
          files={files.filter((f) => f.file.mimeType.startsWith("image/"))}
        />
      )}

      {selectedEmail && (
        <EmailViewer
          email={selectedEmail.email}
          onClose={() => setSelectedEmail(null)}
        />
      )}

      {/* Category Selection Modal */}
      {fileToUpdate && (
        <CategorySelectionModal
          file={fileToUpdate}
          categories={categories}
          categoryIds={categoryIds}
          onClose={() => setFileToUpdate(null)}
          onSave={handleCategorySelect}
        />
      )}
    </div>
  );
};

export default FileManager;
